import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { deliveryPdfAttachment } from '../../utils/pdf.js';

/**
 * E-mails the delivery note PDF via the configured SMTP server (finance
 * /email/send); the body mentions the tracking number when one exists.
 * No status flip - like the order confirmation it is a pure document
 * of an already-posted goods issue. Pass any delivery row
 * (`{id, deliveryNumber}`) - detail incl. lines is fetched here.
 */
export function DeliverySendModal({ delivery, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const open = Boolean(delivery);
  const id = delivery?.id;

  const detail = useQuery({
    queryKey: ['sales', 'deliveries', id],
    queryFn: () => salesApi.getDelivery(id).then((r) => r.items),
    enabled: open,
  });
  const customer = useQuery({
    queryKey: ['sales', 'customer', detail.data?.customerId],
    queryFn: () => salesApi.getCustomer(detail.data.customerId).then((r) => r.items),
    enabled: open && Boolean(detail.data?.customerId),
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
    enabled: open,
  });

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!open || !detail.data) return;
    const d = detail.data;
    setTo(customer.data?.email || '');
    const base = t('delivery.email.subject', { number: d.deliveryNumber });
    setSubject(settings.data?.companyName ? `${base} - ${settings.data.companyName}` : base);
    const tracking = d.trackingNumber
      ? `\n\n${t('delivery.email.tracking', { tracking: d.trackingNumber })}`
      : '';
    setBody(t('delivery.email.body', { number: d.deliveryNumber }) + tracking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail.data?.id, customer.data?.email, settings.data?.companyName]);

  const sendMail = useMutation({
    mutationFn: async () => {
      const att = await deliveryPdfAttachment({
        delivery: detail.data,
        customer: customer.data,
        settings: settings.data,
        t,
      });
      return financeApi.sendEmail({
        to,
        subject,
        text: body,
        attachmentBase64: att.base64,
        attachmentName: att.name,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'deliveries'] });
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('delivery.email.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sendMail.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => sendMail.mutate()} disabled={sendMail.isPending || !to || !detail.data}>
            {sendMail.isPending ? `${t('email.send')}…` : t('email.send')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('email.intro')}</p>
        <label className="field">
          <span className="field-label">{t('email.to')}</span>
          <input className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.subject')}</span>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.message')}</span>
          <textarea className="textarea" rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        {sendMail.isError && <div className="alert danger">{sendMail.error?.message || t('common.loadError')}</div>}
      </div>
    </Modal>
  );
}
